import { Injectable, inject } from '@angular/core'
import dayJS from 'dayjs'
import updateLocale from 'dayjs/plugin/updateLocale'
import { I18nService } from './i18n.service'

dayJS.extend(updateLocale)

interface CalendarConfig {
  lastWeek?: string
  lastDay?: string
  sameDay?: string
  nextDay?: string
  nextWeek: string
  sameElse: string
}

@Injectable()
export class MomentTimeService {
  /** The calendar config */
  public static calendarConfig: CalendarConfig = {
    nextWeek: 'DD/MM/YYYY',
    sameElse: 'DD/MM/YYYY',
  }

  private _i18nService = inject(I18nService)

  /** Convert a dayJS date to a human format for view usage
   * @param { Date } date the date we need to format
   * @param { 'day' | 'hour' | 'minute' } format the format we use to show the date in the view
   * @return { number }
   */
  public dateToFormat(
    date: Date | dayJS.Dayjs,
    format: 'day' | 'hour' | 'minute'
  ): number {
    const newDate = dayJS(date)

    return dayJS().diff(newDate, format)
  }

  /** Inits the moment time service */
  public init(): void {
    switch (this._i18nService.language) {
      case 'fr-FR':
        MomentTimeService.calendarConfig = {
          ...MomentTimeService.calendarConfig,
          lastWeek: 'DD/MM/YYYY',
          lastDay: '[Hier]',
          sameDay: "[Aujourd'hui]",
          nextDay: '[Demain]',
        }
        break
      case 'pt-PT':
        MomentTimeService.calendarConfig = {
          ...MomentTimeService.calendarConfig,
          lastWeek: 'DD/MM/YYYY',
          lastDay: '[Ontem]',
          sameDay: '[Hoje]',
          nextDay: '[Amanhã]',
        }
        break
      case 'zh-Hant-HK':
        MomentTimeService.calendarConfig = {
          ...MomentTimeService.calendarConfig,
          lastWeek: 'DD/MM/YYYY',
          lastDay: '[昨天]',
          sameDay: '[今天]',
          nextDay: '[明天]',
        }
        break
      case 'en-US':
      case 'en-GB':
      default:
        MomentTimeService.calendarConfig = {
          ...MomentTimeService.calendarConfig,
          lastWeek: 'DD/MM/YYYY',
          lastDay: '[Yesterday]',
          sameDay: '[Today]',
          nextDay: '[Tomorrow]',
        }
        break
    }

    dayJS.updateLocale(this._i18nService.language, {
      calendar: MomentTimeService.calendarConfig,
    })
  }

  public getSinceData(date: Date): string {
    const numberOfDaysSinceDate = this.getNumberOfTimeUnitSinceDate(date, 'day')
    if (numberOfDaysSinceDate > 0) {
      return this.getSinceLabel(
        numberOfDaysSinceDate,
        $localize`:Label | days label@@sinceDateCellDayLabel:jour`,
        $localize`:Label | days label@@sinceDateCellDaySLabel:jours`
      )
    }
    const numberOfHoursSinceDate = this.getNumberOfTimeUnitSinceDate(
      date,
      'hour'
    )
    if (numberOfHoursSinceDate > 0) {
      return this.getSinceLabel(
        numberOfHoursSinceDate,
        $localize`:Label | hour label@@sinceDateCellHourLabel:h`
      )
    }
    return this.getSinceLabel(
      this.getNumberOfTimeUnitSinceDate(date, 'minute'),
      $localize`:Label | minute label@@sinceDateCellMinuteLabel:min`
    )
  }

  private getSinceLabel(
    value: number,
    label: string,
    pluralLabel?: string
  ): string {
    return `${value} ${pluralLabel && value > 1 ? pluralLabel : label}`
  }

  private getNumberOfTimeUnitSinceDate(
    date: Date,
    timeUnit: 'day' | 'hour' | 'minute'
  ): number {
    return this.dateToFormat(date, timeUnit)
  }
}
